import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Ticket, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export const PromoCodesManager = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", percent: 10, max_uses: "", expires_at: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/my/promo-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) { toast.error("Choisissez un nom de code"); return; }
    setSaving(true);
    try {
      await api.post("/my/promo-codes", {
        code: form.code,
        percent: Number(form.percent),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      toast.success(`Code ${form.code.toUpperCase()} créé`);
      setForm({ code: "", percent: 10, max_uses: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/my/promo-codes/${c.id}`, { active: !c.active });
      toast.success(c.active ? `Code ${c.code} désactivé` : `Code ${c.code} activé`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const remove = async (c) => {
    if (!confirm(`Supprimer le code ${c.code} ?`)) return;
    try {
      await api.delete(`/my/promo-codes/${c.id}`);
      toast.success("Code supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="bg-white border border-ink/10 rounded-md mt-8" data-testid="promo-codes-manager">
      <div className="p-5 border-b border-ink/10">
        <h2 className="font-heading font-medium text-lg text-ink flex items-center gap-2">
          <Ticket className="h-4 w-4" /> Codes promo
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Offrez une réduction sur vos stands pour accélérer les réservations. La commission de 20% s'applique au montant réduit.
        </p>
      </div>
      <div className="p-5 grid grid-cols-2 lg:grid-cols-5 gap-3 border-b border-ink/10">
        <Input data-testid="promo-code-input" placeholder="Code (ex: EARLY10)" value={form.code}
               onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        <Input data-testid="promo-percent-input" type="number" min={5} max={90} placeholder="% remise" value={form.percent}
               onChange={(e) => setForm({ ...form, percent: e.target.value })} />
        <Input data-testid="promo-maxuses-input" type="number" min={1} placeholder="Utilisations max (optionnel)" value={form.max_uses}
               onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
        <Input data-testid="promo-expires-input" type="date" value={form.expires_at}
               onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
        <Button data-testid="promo-create-btn" onClick={create} disabled={saving}
                className="bg-ink hover:bg-ink/85 text-cream rounded-none">
          <Plus className="h-4 w-4 mr-1" /> Créer
        </Button>
      </div>
      {codes.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground" data-testid="promo-empty">Aucun code promo pour l'instant.</p>
      ) : (
        <div className="divide-y divide-ink/10">
          {codes.map((c) => (
            <div key={c.id} className="p-4 flex items-center justify-between gap-4" data-testid={`promo-row-${c.id}`}>
              <div>
                <p className="font-mono font-bold text-ink">{c.code} <span className="text-accent">−{c.percent}%</span></p>
                <p className="text-xs text-muted-foreground">
                  {c.uses || 0} utilisation{(c.uses || 0) > 1 ? "s" : ""}
                  {c.max_uses ? ` / ${c.max_uses}` : ""}
                  {c.expires_at ? ` · expire le ${new Date(c.expires_at).toLocaleDateString("fr-FR")}` : ""}
                  {!c.active && " · désactivé"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid={`promo-toggle-${c.id}`} checked={c.active} onCheckedChange={() => toggle(c)} />
                <Button data-testid={`promo-delete-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoCodesManager;
